import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";
import { tickSpring } from "../engine/drivers.js";

/**
 * SpringNode
 * ----------
 * Physics-based spring that bounces toward a target value.
 * Wire to any numeric property for organic, bouncy motion.
 */
export class SpringNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Spring";

    this.properties = {
      target: 0,
      tension: 0.3,
      friction: 0.7,
    };

    this._value = 0;
    this._velocity = 0;

    this.addInput("Target", "number");
    this.addOutput("Value", "number");

    addDrivableSlider(this, "Tension", "tension", 0.3, 0.01, 1, 0.01);
    addDrivableSlider(this, "Friction", "friction", 0.7, 0.01, 0.99, 0.01);

    finalizeDrivableSliders(this, 1);

    this.size = [200, 90];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];
    const y = h - 16;

    // Show current value and target
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.font = "9px sans-serif";
    ctx.fillText(`Value: ${this._value.toFixed(2)}`, 10, y);
    ctx.fillText(`Target: ${this.properties.target.toFixed(2)}`, w / 2, y);

    // Spring visualization
    const barY = h - 26;
    const barW = w - 20;
    const range = Math.max(Math.abs(this.properties.target), Math.abs(this._value), 1);
    const targetX = 10 + ((this.properties.target / range + 1) / 2) * barW;
    const valueX = 10 + ((this._value / range + 1) / 2) * barW;

    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(10, barY);
    ctx.lineTo(10 + barW, barY);
    ctx.stroke();

    // Target marker
    ctx.fillStyle = "rgba(255,255,255,0.3)";
    ctx.beginPath();
    ctx.arc(targetX, barY, 3, 0, Math.PI * 2);
    ctx.fill();

    // Value marker
    ctx.fillStyle = "#4E9EFF";
    ctx.beginPath();
    ctx.arc(valueX, barY, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  onExecute() {
    syncDrivableSliders(this);

    const target = this.getInputData(0);
    if (target != null) this.properties.target = target;

    const { tension, friction } = this.properties;

    const result = tickSpring({ value: this._value, velocity: this._velocity }, { target: this.properties.target, tension, friction });
    this._value = result.state.value;
    this._velocity = result.state.velocity;

    this.setOutputData(0, this._value);
  }
}

LiteGraph.registerNodeType("generators/Spring", SpringNode);
